import type { SummaryPart } from './stepSummary.js';
import type { Session, SessionStep } from './workflows.types.js';

export const CONFIGURING_LINE = 'Configuring';
const UPDATE_BRIEF = 'updateIdeationBrief';
const BRIEF = 'brief';

export type MapStatus = 'done' | 'active' | 'pending';

export interface MapOutcome {
  runId: string;
  name: string;
  kind: string;
  date: string | null;
  version?: BriefVersion;
}

/** An updated brief: the run it was made from, and when it landed. */
export interface BriefVersion {
  of: string;
  landedAt: string | null;
}

export interface ChainParent {
  runId: string;
  /** Taken from step order because the service named no parent; never names a brief version. */
  assumed: boolean;
  /** The nearest run up the chain that saved a brief: what an update here replaces. */
  briefRunId: string | null;
}

export interface MapPanel {
  label: string | null;
  parts: SummaryPart[];
}

export interface MapRow {
  key: string;
  title: string;
  line: string | null;
  panel: MapPanel | null;
  status: MapStatus;
  outcome: MapOutcome | null;
  extraOutcomes?: MapOutcome[];
  editable: boolean;
  hidden: boolean;
}

export interface DoneRow {
  key: string;
  title: string;
  line: string | null;
  panel: MapPanel | null;
  outcome: MapOutcome | null;
  extraOutcomes?: MapOutcome[];
  hidden: boolean;
}

export interface RanDisplay {
  title: string;
  line: string | null;
  panel: MapPanel | null;
}

export interface RanStep {
  runId: string;
  display: RanDisplay | null;
}

export interface ActiveStepTitles {
  key: string;
  configTitle: string;
  configurable: boolean;
  resultTitle: string;
  resultHint: string | null;
}

export interface FinishedRun {
  outcome: MapOutcome | null;
  extraOutcomes?: MapOutcome[];
}

export function sessionMapRows(
  history: DoneRow[],
  active: ActiveStepTitles,
  ran: RanStep | null,
  finished: FinishedRun | null,
  canEdit = true,
): MapRow[] {
  const done = history.map((row) => ({
    key: row.key,
    title: row.title,
    line: row.line,
    panel: row.panel,
    status: 'done' as const,
    outcome: row.outcome,
    extraOutcomes: row.extraOutcomes ?? [],
    editable: false,
    hidden: row.hidden,
  }));

  const config: MapRow =
    ran === null
      ? {
          key: `${active.key}-config`,
          title: active.configTitle,
          line: CONFIGURING_LINE,
          panel: null,
          status: 'active',
          outcome: null,
          editable: false,
          hidden: !active.configurable,
        }
      : {
          key: `${active.key}-config`,
          title: ran.display?.title ?? active.configTitle,
          line: ran.display?.line ?? null,
          panel: ran.display?.panel ?? null,
          status: 'done',
          outcome: null,
          editable: canEdit,
          hidden: !active.configurable,
        };

  const result: MapRow = {
    key: `${active.key}-result`,
    title: active.resultTitle,
    line: active.resultHint,
    panel: null,
    status: ran === null ? 'pending' : 'active',
    outcome: finished?.outcome ?? null,
    extraOutcomes: finished?.extraOutcomes ?? [],
    editable: false,
    hidden: false,
  };

  return [...done, config, result];
}

export function visibleRows<Row extends { hidden: boolean }>(rows: Row[]): Row[] {
  return rows.filter((row) => !row.hidden);
}

// A run can save more than one outcome (Hidden Gems: a report and a brief), so a run and a
// kind name one.
export function outcomeKeyOf(outcome: Pick<MapOutcome, 'runId' | 'kind'>): string {
  return `${outcome.runId}|${outcome.kind}`;
}

// One outcome per brief: its newest update that landed. A running or failed update has no
// outcome, so the version before it stays listed. `saved` holds the whole session's outcomes.
export function outcomesOf(rows: MapRow[], saved: MapOutcome[] = []): MapOutcome[] {
  const live = rows.flatMap((row) => [
    ...(row.outcome === null ? [] : [row.outcome]),
    ...(row.extraOutcomes ?? []),
  ]);
  // A run's live outcomes replace all it saved, in the place it saved them.
  const liveRuns = new Set(live.map((outcome) => outcome.runId));
  const savedRuns = new Set(saved.map((outcome) => outcome.runId));
  const placed = new Set<string>();
  const outcomes = [
    ...saved.flatMap((outcome) => {
      if (!liveRuns.has(outcome.runId)) return [outcome];
      if (placed.has(outcome.runId)) return [];
      placed.add(outcome.runId);
      return live.filter((entry) => entry.runId === outcome.runId);
    }),
    ...live.filter((outcome) => !savedRuns.has(outcome.runId)),
  ];
  const byKey = new Map(outcomes.map((outcome) => [outcomeKeyOf(outcome), outcome]));
  const lineageOf = (outcome: MapOutcome): string => {
    let current = outcome;
    const seen = new Set<string>();
    while (current.version !== undefined && !seen.has(outcomeKeyOf(current))) {
      seen.add(outcomeKeyOf(current));
      const sourceKey = outcomeKeyOf({ runId: current.version.of, kind: current.kind });
      const source = byKey.get(sourceKey);
      if (source === undefined) return sourceKey;
      current = source;
    }
    return outcomeKeyOf(current);
  };
  const newest = new Map<string, MapOutcome>();
  for (const outcome of outcomes) {
    const lineage = lineageOf(outcome);
    const kept = newest.get(lineage);
    if (kept === undefined || isNewerVersion(outcome, kept)) newest.set(lineage, outcome);
  }
  return outcomes.filter((outcome) => newest.get(lineageOf(outcome)) === outcome);
}

function isNewerVersion(candidate: MapOutcome, kept: MapOutcome): boolean {
  if (candidate.version === undefined) return false;
  if (kept.version === undefined) return true;
  return landedAt(candidate.version) >= landedAt(kept.version);
}

function landedAt(version: BriefVersion): number {
  return Date.parse(version.landedAt ?? '') || 0;
}

export function briefVersion(
  actionType: string,
  parentRunId: string | null,
  landed: string | null,
): { version?: BriefVersion } {
  return actionType === UPDATE_BRIEF && parentRunId !== null
    ? { version: { of: parentRunId, landedAt: landed } }
    : {};
}

export function savesBrief(
  outcomeKind: string | null,
  extraOutcomes: { kind: string }[] = [],
): boolean {
  return outcomeKind === BRIEF || extraOutcomes.some((extra) => extra.kind === BRIEF);
}

// The nearest step from `runId` up that saved a brief, past steps that saved none (fillElements).
function briefRunOf(steps: SessionStep[], runId: string | null): string | null {
  const seen = new Set<string>();
  let current = runId;
  while (current !== null && !seen.has(current)) {
    seen.add(current);
    const step = steps.find((entry) => entry.runId === current);
    if (step === undefined || savesBrief(step.outcomeKind, step.extraOutcomes)) return current;
    current = step.triggeredByRunId;
  }
  return null;
}

// Only a session's first run chains from nothing, so a later step without a parent comes from a
// service that predates `triggered_by_run_id`.
export function parentOf(steps: SessionStep[], index: number): ChainParent | null {
  const recorded = steps[index]?.triggeredByRunId ?? null;
  if (recorded !== null)
    return { runId: recorded, assumed: false, briefRunId: briefRunOf(steps, recorded) };
  const previous = index > 0 ? steps[index - 1] : undefined;
  return previous === undefined ? null : { runId: previous.runId, assumed: true, briefRunId: null };
}

export function outcomeOf(steps: SessionStep[], step: SessionStep): MapOutcome | null {
  if (step.outcomeKind === null) return null;
  return {
    runId: step.runId,
    name: step.outcomeTitle,
    kind: step.outcomeKind,
    date: outcomeDate(step),
    ...briefVersion(step.actionType, briefRunOf(steps, step.triggeredByRunId), step.completedAt),
  };
}

function extraOutcomesOfStep(step: SessionStep): MapOutcome[] {
  return extraMapOutcomes(
    { runId: step.runId, outcomeKind: step.outcomeKind, outcomeTitle: step.outcomeTitle },
    step.extraOutcomes,
    outcomeDate(step),
  );
}

export function extraMapOutcomes(
  run: { runId: string; outcomeKind: string | null; outcomeTitle: string },
  extras: { kind: string; title: string }[] = [],
  date: string | null,
): MapOutcome[] {
  return extras
    .filter((extra) => extra.kind !== run.outcomeKind)
    .map((extra) => ({
      runId: run.runId,
      name: extra.title || run.outcomeTitle,
      kind: extra.kind,
      date,
    }));
}

// An aside (AI Insights) is listed after the step it was opened on.
export function savedOutcomes(steps: SessionStep[], asides: Session['asides'] = []): MapOutcome[] {
  return steps.flatMap((step) => {
    if (step.status !== 'completed') return [];
    const outcome = outcomeOf(steps, step);
    return [
      ...(outcome === null ? [] : [outcome]),
      ...extraOutcomesOfStep(step),
      ...asides.flatMap((aside) =>
        aside.triggeredByRunId !== step.runId || aside.outcomeKind === null
          ? []
          : [
              {
                runId: aside.runId,
                name: aside.outcomeTitle,
                kind: aside.outcomeKind,
                date: outcomeDate(aside),
              },
            ],
      ),
    ];
  });
}

export function historyFromSession(steps: SessionStep[], currentRunId: string): DoneRow[] {
  const upto = steps.findIndex((step) => step.runId === currentRunId);
  const earlier = upto === -1 ? [] : steps.slice(0, upto);

  return earlier.flatMap((step) => [
    {
      key: `${step.runId}-config`,
      title: step.configTitle,
      line: null,
      panel: null,
      outcome: null,
      hidden: !step.configurable,
    },
    {
      key: `${step.runId}-result`,
      title: step.resultTitle,
      line: step.decision ?? step.summary,
      panel: null,
      outcome: outcomeOf(steps, step),
      extraOutcomes: extraOutcomesOfStep(step),
      hidden: false,
    },
  ]);
}

export function outcomeDate(step: Pick<SessionStep, 'completedAt' | 'startedAt'>): string {
  const at = new Date(step.completedAt ?? step.startedAt);
  const day = String(at.getDate()).padStart(2, '0');
  const month = String(at.getMonth() + 1).padStart(2, '0');
  return `${day}-${month}-${at.getFullYear()}`;
}

export function withOutcomeDates(rows: MapRow[], steps: SessionStep[]): MapRow[] {
  return rows.map((row) => {
    if (row.outcome === null || row.outcome.date !== null) return row;
    const step = steps.find((entry) => entry.runId === row.outcome?.runId);
    return step === undefined
      ? row
      : { ...row, outcome: { ...row.outcome, date: outcomeDate(step) } };
  });
}
